import {
  CredentialsPolicy,
  MPREmbeddedApi,
  MPRLauncherRequest,
} from 'MPREmbedded';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { CORS_POLICY } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';
import { debug } from '@/shared/lib/debug.ts';

import { MPRDebugModel, MPRModel, MPRToolsBar } from '@/features/MPR';

import {
  MPR2Mode,
  MPR2Tool,
  MPR2_EMBEDDED_MODE_BY_TOOL,
  MPR2_TOOLS,
} from '../config';

import { AddToReport } from './AddToReport.tsx';
import styles from './MPR2Widget.module.scss';

const mprEmbedded = new MPREmbeddedApi();

const DEBUG_MPR2 = localStorage.getItem('DEBUG_MPR2') === 'true';

const PASS_ALL_SUBVOLUMES_TO_THE_REQUEST =
  localStorage.getItem('PASS_ALL_SUBVOLUMES_TO_THE_REQUEST') === 'true';

const credentialsPolicy: CredentialsPolicy = (/*url: string*/) => CORS_POLICY;

// Safe removeChild helper function to prevent the "Failed to execute 'removeChild'" error
const safeRemoveChild = (parent: Node | null, child: Node) => {
  if (!parent) return false;

  try {
    if (child && parent && parent.contains(child)) {
      parent.removeChild(child);
      return true;
    }
  } catch (error) {
    debug('Failed to safely remove child:', error);
  }
  return false;
};

export const MPR2Widget = (props: { toothID: string }) => {
  const { toothID } = props;

  const [axesVisible, setAxesVisible] = useState(true);
  const [zoomSynced, setZoomSynced] = useState(false);
  const [MPRMode, setMPRMode] = useState<MPR2Mode>('mainMode');

  const mprLauncherRequest = useAppSelector(
    PASS_ALL_SUBVOLUMES_TO_THE_REQUEST
      ? MPRDebugModel.selectors.selectMPREmbeddedLauncherRequestAllSubVolumes(
          toothID,
        )
      : MPRModel.selectors.selectMPREmbeddedLauncherRequest(toothID),
  );

  const ref = useRef<HTMLDivElement>(null);

  const handleToolClick = (tool: MPR2Tool) => {
    if (tool === 'reset') {
      mprEmbedded.reset();
      mprEmbedded.activateMode('mainMode');
      setMPRMode('mainMode');
      return;
    }

    mprEmbedded.activateMode(MPR2_EMBEDDED_MODE_BY_TOOL[tool]);
    setMPRMode(tool);
  };

  useLayoutEffect(() => {
    const element = mprEmbedded.getElement();

    const container = ref.current;

    if (container) {
      safeRemoveChild(container, element);

      container.appendChild(element);

      mprEmbedded.setCredentialsPolicy(credentialsPolicy);
    }

    return () => {
      safeRemoveChild(container, element);
      mprEmbedded.dispose();
    };
  }, []);

  useEffect(() => {
    if (!mprLauncherRequest) {
      debug(
        '[MPR2] MPR Launcher Request is undefined. Cannot start MPREmbedded',
      );
      return () => {};
    }

    if (DEBUG_MPR2) {
      debug('mprLauncherRequest', mprLauncherRequest);
    }

    mprEmbedded.start(mprLauncherRequest);
  }, [toothID]);

  useEffect(() => {
    if (axesVisible) {
      mprEmbedded.showCrossLines();
    } else {
      mprEmbedded.hideCrossLines();
    }
  }, [axesVisible]);

  useEffect(() => {
    if (zoomSynced) {
      mprEmbedded.enableZoomSync();
    } else {
      mprEmbedded.disableZoomSync();
    }
  }, [zoomSynced]);

  return (
    <>
      <MPRToolsBar
        activeMode={MPRMode}
        isAxesVisible={axesVisible}
        isZoomSynced={zoomSynced}
        screen="toothPage"
        tools={MPR2_TOOLS}
        toothID={toothID}
        onAxesVisibleChange={setAxesVisible}
        onToolClick={handleToolClick}
        onZoomSyncedChange={setZoomSynced}
      />

      {DEBUG_MPR2 && <OpenMPR2Locally request={mprLauncherRequest} />}

      <div ref={ref} className={styles.mprEmbeddedContainer} />

      <AddToReport toothID={toothID} mprEmbedded={mprEmbedded} />
    </>
  );
};

const OpenMPR2Locally = (props: { request: MPRLauncherRequest | null }) => {
  const href = useMemo(() => {
    const params: Record<string, string> = {
      request: JSON.stringify(props.request),
      accessToken: localStorage.getItem('accessToken')!,
    };

    return `http://localhost:3002/mpr?${new URLSearchParams(params).toString()}`;
  }, [props.request]);

  return (
    <a href={href} target="_blank">
      Open MPR2 Locally
    </a>
  );
};
